import { useState, useEffect } from 'react'
import { Users, GraduationCap, Percent, RefreshCw, ExternalLink, Award, Download, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { useEstadisticasReporte } from '@/modules/registros'
import { webinarsAnalyticsApi } from '../api/webinars-analytics.api'
import { useWebinarReporteGrupoFarma } from '../hooks/useWebinarReporte'
import { HorizontalBarChart } from '../components/HorizontalBarChart'
import { VerticalBarChart } from '../components/VerticalBarChart'
import { ConversionFunnel, type FunnelStep } from '@/modules/multichannel/components/ConversionFunnel'

const REGISTROS_CONFIG_ID = 41

const AZUL  = '#57a6d8'
const VERDE = '#2fbc80'

const FILTROS = ['todos', 'Video 1', 'Video 2', 'Video 3'] as const
type Filtro = (typeof FILTROS)[number]

function AnimatedCounter({ value }: { value: number }) {
  const [display, setDisplay] = useState(0)

  useEffect(() => {
    if (value <= 0) { setDisplay(value); return }
    const duration = 1200
    const steps = Math.ceil(duration / 16)
    let step = 0
    const timer = setInterval(() => {
      step++
      setDisplay(Math.round((value * step) / steps))
      if (step >= steps) { setDisplay(value); clearInterval(timer) }
    }, 16)
    return () => clearInterval(timer)
  }, [value])

  return <span>{display.toLocaleString('es-EC')}</span>
}

interface KpiCardProps {
  icon: React.ElementType
  label: string
  value: React.ReactNode
  sub: string
  color: string
}

function KpiCard({ icon: Icon, label, value, sub, color }: KpiCardProps) {
  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${color}22 0%, ${color}08 50%, transparent 100%)` }}
    >
      <div className="px-5 py-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">{label}</p>
          <div
            className="h-9 w-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${color}18` }}
          >
            <Icon className="h-5 w-5" style={{ color }} />
          </div>
        </div>
        <div className="text-3xl font-bold text-foreground leading-none mb-1.5">{value}</div>
        <p className="text-xs text-muted-foreground">{sub}</p>
      </div>
      <div className="h-1 w-full" style={{ background: `linear-gradient(to right, ${color}, ${color}40)` }} />
    </div>
  )
}

export function WebinarsGrupoFarmaPage() {
  const { data, isLoading, error } = useWebinarReporteGrupoFarma()
  const { data: estadisticas } = useEstadisticasReporte(REGISTROS_CONFIG_ID)
  const [filtro, setFiltro] = useState<Filtro>('todos')
  const [exportando, setExportando] = useState(false)

  const handleExportar = async () => {
    setExportando(true)
    try {
      const blob = await webinarsAnalyticsApi.exportarGrupoFarma()
      const url = window.URL.createObjectURL(blob)
      const fecha = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      const link = document.createElement('a')
      link.href = url
      link.download = `usuarios_webinars_grupofarma_${fecha}.xlsx`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      alert('No se pudo exportar el reporte. Intente nuevamente.')
    } finally {
      setExportando(false)
    }
  }

  if (isLoading) {
    return (
      <div className="p-4 space-y-4 max-w-screen-xl mx-auto">
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="p-4 space-y-4 max-w-screen-xl mx-auto">
        <div className="flex items-center justify-center py-32">
          <p className="text-destructive text-sm text-center">
            No se pudo cargar el reporte. Verifique que el Google Sheets esté disponible e
            intente nuevamente.
          </p>
        </div>
      </div>
    )
  }

  const { cards } = data
  const distrib = data.distribuciones[filtro] ?? data.distribuciones['todos']

  const asistentesPorWebinar = cards.webinars.map((w) => ({
    label: w.nombre,
    valor: w.participantes_unicos,
  }))

  // Total de registros (inscritos) para conversión y embudo.
  const totalRegistros = estadisticas?.total_registros ?? 0
  const conversion =
    totalRegistros > 0
      ? Math.round((cards.total_unicos / totalRegistros) * 1000) / 10
      : 0

  // Embudo de conversión: Registros → Participantes → Graduados (% sobre la base).
  const pctGraduadosBase =
    totalRegistros > 0 ? Math.round((cards.graduados / totalRegistros) * 1000) / 10 : 0
  const embudoConversion: FunnelStep[] = [
    { label: 'Registros', value: totalRegistros, percentage: 100, color: '#1e40af' },
    { label: 'Participantes', value: cards.total_unicos, percentage: conversion, color: '#2563eb' },
    { label: 'Graduados', value: cards.graduados, percentage: pctGraduadosBase, color: '#3b82f6' },
  ]

  const engagementData = [
    { label: 'Solo 1 video',         valor: cards.un_video,   color: '#4ade80' }, // green-400
    { label: 'Solo 2 videos',        valor: cards.dos_videos, color: '#4ade80' }, // green-400
    { label: 'Graduados (3 videos)', valor: cards.graduados,  color: VERDE },
  ]

  return (
    <div className="p-4 space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold">{data.titulo}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs">{data.subtitulo}</Badge>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <RefreshCw className="h-3 w-3" /> auto-refresh 5 min
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3].map((n) => (
            <a
              key={n}
              href={`/visualizaciones/${n}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-800 hover:bg-blue-900 text-white transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Video {n}
            </a>
          ))}
          <button
            onClick={handleExportar}
            disabled={exportando}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {exportando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {exportando ? 'Exportando…' : 'Exportar usuarios'}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={Users}
          label="Participantes únicos"
          value={<AnimatedCounter value={cards.total_unicos} />}
          sub={`de ${totalRegistros.toLocaleString('es-EC')} registros`}
          color={AZUL}
        />
        <KpiCard
          icon={Percent}
          label="Conversión"
          value={`${conversion}%`}
          sub={`${cards.total_unicos.toLocaleString('es-EC')} participaron`}
          color={AZUL}
        />
        <KpiCard
          icon={GraduationCap}
          label="Graduados (3 videos)"
          value={<AnimatedCounter value={cards.graduados} />}
          sub={`${cards.porcentaje_graduados}% del total de participantes`}
          color={VERDE}
        />
        <KpiCard
          icon={Award}
          label="Certificados"
          value={<AnimatedCounter value={cards.certificados} />}
          sub={`${cards.graduados > 0 ? ((cards.certificados / cards.graduados) * 100).toFixed(1) : 0}% de graduados`}
          color={VERDE}
        />
      </div>

      {/* Engagement barras + Embudo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <Card className="border border-gray-300 dark:border-gray-700 flex flex-col">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Distribución de engagement</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col space-y-4">
            {engagementData.map(({ label, valor, color }) => {
              const pct = cards.total_unicos > 0 ? (valor / cards.total_unicos) * 100 : 0
              return (
                <div key={label} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-foreground">{label}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">{valor.toLocaleString('es-EC')}</span>
                      <span className="text-xs font-semibold w-12 text-right" style={{ color }}>{pct.toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className="h-2.5 w-full rounded-full bg-gray-100 dark:bg-gray-800">
                    <div
                      className="h-2.5 rounded-full transition-all duration-700"
                      style={{ width: `${pct}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              )
            })}

            <div className="border-t border-gray-100 dark:border-gray-800 pt-4 flex-1 flex flex-col">
              <p className="text-xs font-medium text-muted-foreground mb-2">Participantes únicos por video</p>
              <div className="flex-1" style={{ minHeight: 160 }}>
                <VerticalBarChart
                  data={asistentesPorWebinar}
                  color={AZUL}
                  seriesLabel="Participantes únicos"
                  height={220}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <ConversionFunnel
          customSteps={embudoConversion}
          base={totalRegistros}
          entrega={0}
          aperturas={0}
          clics={0}
          tasa_entrega={0}
          tasa_aperturas={0}
          tasa_clics={0}
        />
      </div>

      {/* Filtro por video */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3">
            <span className="text-sm font-semibold">Filtrar por video:</span>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {FILTROS.map((f) => (
                <button
                  key={f}
                  onClick={() => setFiltro(f)}
                  className={`py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    filtro === f
                      ? 'bg-blue-600 text-white'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70'
                  }`}
                >
                  {f === 'todos' ? 'Todos' : f}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gráficas de visualizaciones por dimensión (barras horizontales con scroll interno) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Visualizaciones por Visitador</CardTitle>
          </CardHeader>
          <CardContent>
            <HorizontalBarChart data={distrib.visitador} color={AZUL} height={340} />
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Visualizaciones por Especialidad</CardTitle>
          </CardHeader>
          <CardContent>
            <HorizontalBarChart data={distrib.especialidad} color={AZUL} height={340} />
          </CardContent>
        </Card>

        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Visualizaciones por Ciudad</CardTitle>
          </CardHeader>
          <CardContent>
            <HorizontalBarChart data={distrib.ciudad} color={AZUL} height={340} />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
